import React from 'react';
import { AlertTriangleIcon, GiftIcon } from 'lucide-react';
import { Section } from './Shared';
import { DnaHelix, Mandala, TreeOfLife } from './BioDecor';
import { bonuses, tomes } from '../data/kitContents';
const accentStyles: Record<'blue' | 'green' | 'gold', {
  badge: string;
  title: string;
  check: string;
}> = {
  blue: {
    badge: 'bg-brand-blue text-brand-goldLight',
    title: 'text-brand-blue',
    check: 'text-brand-blueMid'
  },
  green: {
    badge: 'bg-brand-green text-brand-goldLight',
    title: 'text-brand-green',
    check: 'text-brand-greenMid'
  },
  gold: {
    badge: 'bg-brand-goldDark text-brand-goldLight',
    title: 'text-brand-goldDark',
    check: 'text-brand-gold'
  }
};
const valueItems: string[] = ['📘 Tomo I: Documentación y marco profesional', '📗 Tomo II: Anamnesis y exploración estructurada', '📕 Tomo III: Prácticas de integración', '🎁 Cuaderno del Paciente', '🎁 Bitácora de 21 Días', '🎁 Radar de 50 Preguntas Gatillo™', '🎁 Guía de Comunicación Ética', '🎁 Filtro Rojo'];
export function PackContents() {
  return <Section id="contenido" className="relative" data-id="element-259" data-mp="3p51ouocu8ihc">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" data-id="element-260" data-mp="3k9cr8qkdpbno">
        <TreeOfLife className="absolute right-[3%] top-8 h-40 w-24 text-brand-sageMist" data-id="element-261" data-mp="2sle4ehxuuy2" />
        <DnaHelix className="absolute left-[2%] top-1/2 h-48 w-20 text-brand-blueMist" data-id="element-262" data-mp="3u5g0shhk8ckn" />
      </div>

      <div className="mb-4 text-center" data-id="element-263" data-mp="brgkprowa71w">
        <p className="mb-2 text-sm font-bold uppercase tracking-[.18em] text-brand-goldDark" data-id="element-264" data-mp="buksez1kjkxb">✨ Qué recibes</p>
        <h2 className="font-display text-3xl font-bold text-brand-blue md:text-5xl" data-id="element-265" data-mp="6vrj7itbeg2y">
          3 Tomos Principales + 5 Bonus Estratégicos
        </h2>
        <div className="mt-4 flex flex-wrap justify-center gap-3 text-sm font-semibold text-brand-textLight md:text-base" data-id="element-266" data-mp="gsw4zhj909j8">
          <span className="rounded-full border border-brand-blue/20 bg-white px-4 py-1.5" data-id="element-267" data-mp="2niaq8jr9c2n2">📚 3 Tomos + 5 Bonus</span>
          <span className="rounded-full border border-brand-gold/40 bg-white px-4 py-1.5" data-id="element-268" data-mp="2ilam4edyx9yl">📖 +150 páginas</span>
        </div>
      </div>

      <p className="mx-auto mb-10 mt-6 max-w-2xl text-center text-sm leading-relaxed text-brand-textLight md:text-base" data-id="element-269" data-mp="3c7wx2y660cdy">
        Un sistema práctico para dejar de tener información dispersa y{' '}
        <strong className="text-brand-greenMid" data-id="element-270" data-mp="3m74lqhicntgd">comenzar a trabajar con recursos organizados</strong>.
      </p>

      <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-3" data-id="element-271" data-mp="3r4wdnbizvfkk">
        {tomes.map(tome => {
        const accent = accentStyles[tome.accent];
        return <article key={tome.label} className="paper-panel flex h-full flex-col rounded-3xl p-6 md:p-7" data-id="element-272" data-mp="3svc5u5fkyn6">
              <div className="mb-4 flex gap-4" data-id="element-273" data-mp="8ryf991uousi">
                <img src={tome.cover} alt={tome.coverAlt} loading="lazy" draggable={false} className="h-[150px] w-[106px] shrink-0 rounded-lg object-cover object-top shadow-[0_10px_22px_rgba(8,32,60,.2)] ring-1 ring-brand-blue/15" data-id="element-274" data-mp="8qn6eutjswuj" />
                <div className="min-w-0" data-id="element-275" data-mp="inrrerbnqm9r">
                  <span className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-[11px] font-bold tracking-[.18em] ring-1 ring-brand-gold/40 ${accent.badge}`} data-id="element-276" data-mp="238gdtg1zkuu3">
                    <span aria-hidden="true" data-id="element-277" data-mp="1t9qvjm66h8db">{tome.emoji}</span>
                    {tome.label}
                  </span>
                  <h3 className={`mt-3 font-serif text-lg font-bold leading-tight md:text-xl ${accent.title}`} data-id="element-278" data-mp="1obpet7w85l50">
                    {tome.title}
                  </h3>
                  <p className="mt-1.5 text-xs italic leading-snug text-brand-muted" data-id="element-279" data-mp="2gzl61x30ykue">{tome.subtitle}</p>
                </div>
              </div>
              <div className="leaf-rule mb-4" aria-hidden="true" data-id="element-280" data-mp="2c7thfs3fip6w" />
              <ul className="space-y-2.5 text-sm leading-snug text-brand-text" data-id="element-281" data-mp="27a1l8lvr4gww">
                {tome.features.map(feature => <li key={feature} className="flex gap-2.5" data-id="element-282" data-mp="34pp7bxsqq8i8">
                    <span className={`font-bold ${accent.check}`} data-id="element-283" data-mp="34r0fe9t7y2f5">✓</span>
                    <span data-id="element-284" data-mp="2zww2zb16p26o">{feature}</span>
                  </li>)}
              </ul>
              {tome.note && <p className="mt-4 rounded-xl border border-brand-gold/40 bg-brand-goldLight/25 px-4 py-3 text-sm font-medium leading-relaxed text-brand-text" data-id="element-285" data-mp="2keuvvqy8pofj">
                  {tome.note}
                </p>}
              {tome.warning && <p className="mt-3 flex gap-2 rounded-xl border border-brand-blue/15 bg-brand-blueMist/60 px-4 py-3 text-[12px] leading-relaxed text-brand-textLight" data-id="element-286" data-mp="2akmf20iknjx7">
                  <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand-goldDark" data-id="element-287" data-mp="337fkgw6wp7dj" />
                  <span data-id="element-288" data-mp="384p8c7endaz9">{tome.warning}</span>
                </p>}
            </article>;
      })}
      </div>

      <div className="mb-6 mt-14 text-center" data-id="element-289" data-mp="2tgkxfplu63r1">
        <h3 className="font-serif text-xl font-bold text-brand-blue md:text-2xl" data-id="element-290" data-mp="2yfxiq5x272ri">
          🎁 Y ADEMÁS RECIBES 5 BONUS — <span className="text-brand-goldDark" data-id="element-291" data-mp="3m59nmtlcsoo0">SIEMPRE INCLUIDOS</span>
        </h3>
      </div>

      <div className="mx-auto grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-292" data-mp="3qx1hpe3pxciw">
        {bonuses.map(bonus => <article key={bonus.number} className="flex gap-4 rounded-2xl border border-brand-blue/15 bg-brand-blueMist/50 p-4" data-id="element-293" data-mp="1fgzopryruh4t">
            <img src={bonus.cover} alt={bonus.coverAlt} loading="lazy" draggable={false} className="h-[124px] w-[88px] shrink-0 rounded-lg object-cover object-top shadow-[0_8px_18px_rgba(8,32,60,.18)] ring-1 ring-brand-blue/15" data-id="element-294" data-mp="1ahn0b2s95204" />
            <div className="flex min-w-0 flex-col" data-id="element-295" data-mp="1kac6bi519no2">
              <div className="mb-2 flex flex-wrap items-center gap-2" data-id="element-296" data-mp="wjq1s0u8mlit">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-blue px-2.5 py-1 text-[10px] font-bold tracking-wide text-brand-goldLight" data-id="element-297" data-mp="rjtxe0hmos2q">
                  <GiftIcon className="h-3 w-3" data-id="element-298" data-mp="15h8pjcz9ndye" />
                  BONUS {bonus.number}
                </span>
                <span className="text-[11px] font-semibold text-brand-goldDark" data-id="element-299" data-mp="11k4bdnbhm3iy">{bonus.pages}</span>
              </div>
              <p className="text-[15px] font-bold leading-tight text-brand-blue" data-id="element-300" data-mp="2cx6qedxxul10">{bonus.title}</p>
              <p className="mt-2 text-sm leading-relaxed text-brand-textLight" data-id="element-301" data-mp="19m9ecckawf47">{bonus.description}</p>
              <ul className="mt-3 space-y-1.5 text-[13px] leading-snug text-brand-text" data-id="element-302" data-mp="1eku5znlhlqh1">
                {bonus.features.map(feature => <li key={feature} className="flex gap-2" data-id="element-303" data-mp="1jjxb181gvwp0">
                    <span className="font-bold text-brand-blueMid" data-id="element-304" data-mp="1jicc7684drlc">✓</span>
                    <span data-id="element-305" data-mp="1nkzb1r37kcs1">{feature}</span>
                  </li>)}
              </ul>
            </div>
          </article>)}
      </div>

      <div className="mx-auto mt-14 max-w-4xl" data-id="element-306" data-mp="vum7xjwn9q7p">
        <div className="paper-panel rounded-3xl p-6 md:p-9" data-id="element-307" data-mp="vve34tmbxlxg">
          <h3 className="text-center font-display text-2xl font-bold text-brand-blue md:text-3xl" data-id="element-308" data-mp="zwg0f4wornws">
            Todo esto dentro de un solo sistema
          </h3>
          <div className="leaf-rule mx-auto my-5 max-w-xs" aria-hidden="true" data-id="element-309" data-mp="14p0u86fle10b" />
          <ul className="grid gap-3 sm:grid-cols-2" data-id="element-310" data-mp="84ieg5d676uf">
            {valueItems.map(item => <li key={item} className="rounded-xl border border-brand-blue/15 bg-white px-4 py-3 text-sm font-medium text-brand-text" data-id="element-311" data-mp="c5k4pwp408k9">
                {item}
              </li>)}
          </ul>
          <p className="mt-6 text-center font-serif text-lg font-bold text-brand-greenMid md:text-xl" data-id="element-312" data-mp="3vt5lc09okza2">
            3 TOMOS + 5 BONUS · Más de 150 páginas de herramientas digitales
          </p>
        </div>
      </div>

      <div className="relative mt-10 text-center" data-id="element-313" data-mp="3mrxnfjb2u0gi">
        <Mandala aria-hidden="true" className="pointer-events-none absolute -top-6 left-1/2 h-10 w-10 -translate-x-1/2 text-brand-amber/40" data-id="element-314" data-mp="3htmfje4ce00x" />
        <p className="inline-block rounded-full border border-brand-gold/60 bg-white px-5 py-3 text-sm font-medium text-brand-text" data-id="element-315" data-mp="3d24aoez77fao">
          Todo el valor de los bonus{' '}
          <span className="font-bold text-brand-greenMid" data-id="element-316" data-mp="342r908hbfv0i">incluido con tu Kit Maestro de Biodescodificación™</span>
        </p>
      </div>
    </Section>;
}